import { useEffect, useState } from "react";
import { Banknote, Zap, Loader2, MapPin, Target, Clock, Sparkles, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { RemittancePartners } from "@/components/RemittancePartners";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function MoneyEnginePage() {
  const [form, setForm] = useState({
    skill: "",
    city: "London",
    weekly_income_target: 200,
    time_available: "2 hours/day evenings",
    experience_level: "beginner",
  });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/money/playbook").then((r) => {
      if (r.data?.exists) {
        setData(r.data.playbook);
        setForm({
          skill: r.data.skill || "",
          city: r.data.city || "London",
          weekly_income_target: r.data.weekly_income_target || 200,
          time_available: r.data.time_available || "2 hours/day evenings",
          experience_level: r.data.experience_level || "beginner",
        });
      }
    }).catch(() => {});
  }, []);

  const generate = async () => {
    if (!form.skill || form.skill.length < 2) {
      toast.error("Tell me your skill or hustle first");
      return;
    }
    setBusy(true);
    setData(null);
    try {
      const { data: r } = await api.post("/money/playbook", {
        ...form,
        weekly_income_target: Number(form.weekly_income_target),
      });
      setData(r);
      toast.success("Your Money Engine is ready 🔥");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate plan — try again");
    } finally { setBusy(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:px-8">
      <div className="flex items-center gap-2 text-emerald-400">
        <Banknote className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Money Engine</span>
      </div>
      <h1 className="mt-3 font-display text-3xl tracking-tight sm:text-4xl">Get Paid This Week</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Tell me your skill, your city and your target. I&apos;ll build a 7-day plan, ready-to-send scripts,
        the best platforms for you and a UK pricing guide — in under 60 seconds.
      </p>

      {/* Input form */}
      <div className="mt-8 grid grid-cols-1 gap-3 rounded-2xl border border-border bg-card p-6 md:grid-cols-2" data-testid="money-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Your skill / hustle</label>
          <Input
            value={form.skill}
            onChange={(e) => setForm({ ...form, skill: e.target.value })}
            placeholder="e.g. graphic design, parcel delivery, tutoring maths"
            data-testid="money-skill"
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">City</label>
          <Input
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="London"
            data-testid="money-city"
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Weekly income target (£)</label>
          <Input
            type="number"
            value={form.weekly_income_target}
            onChange={(e) => setForm({ ...form, weekly_income_target: e.target.value })}
            data-testid="money-target"
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Time available</label>
          <Input
            value={form.time_available}
            onChange={(e) => setForm({ ...form, time_available: e.target.value })}
            placeholder="2 hours/day evenings"
            data-testid="money-time"
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Experience level</label>
          <Select value={form.experience_level} onValueChange={(v) => setForm({ ...form, experience_level: v })}>
            <SelectTrigger className="mt-1" data-testid="money-level"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button
            onClick={generate}
            disabled={busy}
            className="h-11 w-full bg-emerald-500 text-white hover:bg-emerald-600"
            data-testid="money-generate"
          >
            {busy ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Building your plan...</>) : (<><Zap className="mr-2 h-4 w-4" /> Build my Money Engine</>)}
          </Button>
        </div>
      </div>

      {/* Results */}
      {data && (
        <div className="mt-10 space-y-8" data-testid="money-results">
          {/* Income targets */}
          {data.income_targets && (
            <div className="grid grid-cols-3 gap-3">
              {["weekly", "monthly", "yearly"].map((k) => (
                <div key={k} className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-center">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{k}</div>
                  <div className="mt-1 font-display text-2xl text-emerald-400">£{Number(data.income_targets[k] || 0).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}

          {/* 7-day plan */}
          {data.plan?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl">Your 7-day plan</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                {data.plan.map((d) => (
                  <div key={d.day} className="rounded-xl border border-border bg-card p-5" data-testid={`money-day-${d.day}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">Day {d.day}</span>
                      {d.target_amount > 0 && <span className="text-xs font-semibold text-emerald-400">£{d.target_amount}</span>}
                    </div>
                    <h3 className="mt-2 font-display text-lg">{d.title}</h3>
                    <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                      {(d.actions || []).map((a, i) => <li key={i}>• {a}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Scripts */}
          {data.scripts && (
            <section>
              <h2 className="font-display text-2xl">Ready-to-send scripts</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                {Object.entries(data.scripts).map(([channel, text]) => (
                  <div key={channel} className="rounded-xl border border-border bg-card p-5" data-testid={`script-${channel}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">{channel.replace("_", " ")}</span>
                      <Button size="sm" variant="ghost" onClick={() => copy(text)} className="h-7 px-2 text-xs">
                        <Copy className="mr-1 h-3 w-3" /> Copy
                      </Button>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm">{text}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Platforms */}
          {data.platforms?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl">Best platforms for you</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                {data.platforms.map((p, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-5" data-testid={`platform-${i}`}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <h3 className="font-display text-lg">{p.name}</h3>
                    </div>
                    <span className="mt-1 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary">{p.category}</span>
                    <p className="mt-2 text-sm text-muted-foreground">{p.why}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Pricing guide */}
          {data.pricing_guide?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl">UK pricing guide</h2>
              <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-background/40">
                      <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Service</th>
                      <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Price range</th>
                      <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pricing_guide.map((row, i) => (
                      <tr key={i} className="border-b border-border last:border-0" data-testid={`pricing-${i}`}>
                        <td className="px-4 py-3 font-medium">{row.service}</td>
                        <td className="px-4 py-3 font-semibold text-emerald-400">£{row.price_low} – £{row.price_high}</td>
                        <td className="px-4 py-3 text-muted-foreground">{row.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      <RemittancePartners />
    </div>
  );
}
